import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Check, Loader2, ArrowLeft, Sparkles } from 'lucide-react';

const fmtPrice = (usd) => {
  if (usd === 0) return '$0';
  if (usd == null) return 'Custom';
  return `$${usd.toLocaleString()}`;
};

export default function PlanPicker() {
  const navigate = useNavigate();
  const acct = getCurrentAccount();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/subscription/plans');
        setPlans(data?.plans || []);
      } catch (e) {
        toast.error('Could not load plans');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pickFree = async () => {
    setBusy('free');
    try {
      await api.post('/subscription/start-free', { account_id: acct?.id });
      toast.success('Free plan active — welcome!');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate Free plan');
    } finally {
      setBusy(null);
    }
  };

  const pickPaid = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
        account_id: acct?.id,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  const pickEnterprise = () => {
    window.dispatchEvent(new CustomEvent('ai-agent:open', {
      detail: { tab: 'chats', prompt: "I'm interested in the Enterprise plan. Can you help me get in touch with sales?" },
    }));
  };

  const onSelect = (plan) => {
    if (plan.cta_url) { window.open(plan.cta_url, '_blank', 'noopener'); return; }
    if (plan.id === 'free') return pickFree();
    if (plan.id === 'enterprise') return pickEnterprise();
    return pickPaid(plan.id);
  };

  const ctaLabel = (plan) => {
    if (plan.cta_url) return 'Contact us';
    if (plan.id === 'free') return 'Start free';
    if (plan.id === 'enterprise') return 'Talk to sales';
    return `Choose ${plan.label}`;
  };

  const gridCols = plans.length >= 4 ? 'lg:grid-cols-4' : plans.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2';

  return (
    <div className="min-h-screen bg-white" data-testid="plan-picker-page">
      <div className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-8 w-8" />
            <span className="text-lg font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>BillPortal</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500 hover:text-primary"
            data-testid="plan-picker-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-primary font-semibold mb-3">
          <Sparkles className="h-3.5 w-3.5" /> Plans & pricing
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Choose the plan that<br /><span className="text-primary">fits your volume.</span>
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl text-[15px] leading-relaxed">
          Setup is always free — unlimited properties, meters and portal connections. You only pay for the bills you process each month.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading plans…
          </div>
        ) : (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-0 border border-slate-200 mt-10`}>
            {plans.map((plan, idx) => {
              const highlight = plan.id === 'starter';
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`flex flex-col p-7 ${idx > 0 ? 'border-t md:border-t-0 md:border-l border-slate-200' : ''} ${highlight ? 'bg-slate-900 text-white' : 'bg-white'}`}
                >
                  {highlight && (
                    <div className="text-[10px] uppercase tracking-widest text-blue-300 font-semibold mb-2">Most popular</div>
                  )}
                  <div className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{plan.label}</div>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>{fmtPrice(plan.usd_per_month)}</span>
                    {plan.usd_per_month != null && plan.usd_per_month > 0 && (
                      <span className={`text-sm font-medium mb-1 ${highlight ? 'text-slate-300' : 'text-slate-400'}`}>/mo</span>
                    )}
                  </div>
                  <p className={`mt-3 text-xs leading-relaxed min-h-[48px] ${highlight ? 'text-slate-300' : 'text-slate-500'}`}>{plan.tagline}</p>

                  <Button
                    onClick={() => onSelect(plan)}
                    disabled={busy === plan.id}
                    data-testid={`plan-card-cta-${plan.id}`}
                    className={`rounded-none mt-2 h-11 font-bold uppercase tracking-wide text-xs ${highlight ? 'bg-white text-slate-900 hover:bg-slate-100' : ''}`}
                    variant={highlight ? 'default' : plan.id === 'free' ? 'outline' : 'default'}
                  >
                    {busy === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : ctaLabel(plan)}
                  </Button>

                  <ul className="mt-6 space-y-2.5">
                    {(plan.bullets || []).map((b, i) => (
                      <li key={i} className={`flex items-start gap-2 text-[13px] leading-snug ${highlight ? 'text-slate-200' : 'text-slate-600'}`}>
                        <Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${highlight ? 'text-blue-300' : 'text-primary'}`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-6 text-[11px] text-slate-400">
          Paid plans are billed monthly. You can change or cancel anytime from the Billing area.
        </p>
      </div>
    </div>
  );
}
